import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORY_LABELS, MONTH_NAMES } from "../lib/format";

const statusVariant = (status) => {
  if (status === "Federal Holiday") return "tag--solid";
  if (status === "Heritage Month") return "tag--accent";
  return "";
};

export const DateCard = ({ entry, onOpen, variant = "default" }) => {
  const monthLabel = entry.month ? MONTH_NAMES[entry.month] : "";
  const dayLabel = entry.day ? entry.day : "";

  return (
    <article
      data-testid="date-card"
      data-entry-id={entry.id}
      onClick={() => onOpen?.(entry)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(entry); } }}
      tabIndex={0}
      role="button"
      aria-label={`Read more about ${entry.title}`}
      style={{
        position: "relative",
        background: "hsl(var(--bg-elev))",
        border: "1px solid hsl(var(--line))",
        padding: variant === "compact" ? "1.25rem" : "1.6rem 1.6rem 1.4rem",
        cursor: "pointer",
        transition: "border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease",
        display: "flex",
        flexDirection: "column",
        gap: "0.9rem",
        height: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "hsl(var(--accent))";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "hsl(var(--line))";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <div className="archival-figure" style={{
          fontFamily: "var(--font-display)",
          fontSize: "0.95rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "hsl(var(--accent))",
          fontWeight: 500,
        }}>
          {monthLabel ? `${monthLabel}${dayLabel ? " " + dayLabel : ""}` : entry.date_label}
        </div>
        <span className={`tag ${statusVariant(entry.recognition_status)}`} style={{ flexShrink: 0 }}>
          {entry.recognition_status}
        </span>
      </header>

      <h3 style={{
        fontFamily: "var(--font-display)",
        fontSize: variant === "compact" ? "1.2rem" : "1.45rem",
        lineHeight: 1.15,
        fontWeight: 500,
        margin: 0,
        color: "hsl(var(--ink))",
      }}>
        {entry.title}
      </h3>

      <p style={{
        fontSize: "0.93rem",
        lineHeight: 1.55,
        color: "hsl(var(--ink-soft))",
        margin: 0,
        display: "-webkit-box",
        WebkitLineClamp: variant === "compact" ? 3 : 4,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}>
        {entry.short_description}
      </p>

      <footer style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "1rem", marginTop: "auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(entry.categories || []).slice(0, 2).map((c) => (
            <span key={c} style={{
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "hsl(var(--muted))",
            }}>
              {CATEGORY_LABELS[c] || c}
            </span>
          ))}
        </div>
        <Link
          to={`/date/${entry.id}`}
          aria-label={`Open the full page for ${entry.title}`}
          data-testid="card-page-link"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: "0.72rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 500,
            color: "hsl(var(--accent))",
            textDecoration: "none",
            padding: "0.3rem 0.55rem",
            border: "1px solid hsl(var(--accent) / 0.45)",
            borderRadius: 999,
            transition: "background-color 160ms ease, color 160ms ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "hsl(var(--accent))"; e.currentTarget.style.color = "hsl(var(--accent-ink))"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "hsl(var(--accent))"; }}
        >
          Read <ArrowUpRight size={12} aria-hidden />
        </Link>
      </footer>
    </article>
  );
};
